import {
  ChevronLeft,
  ChevronRight,
  Clock,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";
import { ProgressBar } from "../ProgressBar";
import { Button } from "@shared/ui/button";
import { PageHeader } from "@shared/components/PageHeader";

const MigrationProgress = ({
  migrationData,
  migrationLogs,
  errorLogs,
  onPrevious,
  onNext,
}) => {
  // ✅ FIXED: Handle undefined migrationData and fix property names
  const {
    status = "processing",
    processed = 0,
    total = 0,
    completionPercentage = 0,
    processingTime = "0m 0s",
    successRate = 0,
    errorRate = 0,
  } = migrationData || {};

  // ✅ FIXED: Use successRate for progress bar instead of completion_percentage
  const progressPercentage = isNaN(completionPercentage)
    ? 0
    : Math.round(completionPercentage);
  // ✅ FIXED: Better completion detection
  const completedStatuses = [
    "completed",
    "finished",
    "success",
    "failed",
    "error",
    "permanently_failed",
  ];

  const successStatuses = ["completed", "finished", "success"];

  const visibilityForPreviousButton =
    completionPercentage >= 100 || successStatuses.includes(status);

  const isCompleted =
    completionPercentage >= 100 || completedStatuses.includes(status);

  const hasErrors = errorLogs && errorLogs.length > 0;

  // ✅ NEW: Determine progress bar color based on success rate and errors
  const getProgressBarColor = () => {
    if (isCompleted) {
      if (hasErrors && errorRate > 10) return "bg-red-500"; // High error rate
      if (hasErrors && errorRate > 5) return "bg-yellow-500"; // Medium error rate
      if (hasErrors) return "bg-orange-500"; // Low error rate
      return "bg-green-600"; // No errors
    }
    return "bg-blue-600"; // In progress
  };

  return (
    <div className="min-h-screen w-full  p-6">
      <ProgressBar
        currentStep={6}
        totalSteps={7}
        progress={84}
        currentStepName={"Migration Report"}
      />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <PageHeader
            title="Migration Execution"
            subtitle="Execute the migration process and monitor its progress."
          />
        </div>

        {/* Progress Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div
                className={`p-2 rounded-lg ${
                  isCompleted
                    ? hasErrors
                      ? "bg-yellow-100"
                      : "bg-green-100"
                    : "bg-blue-100"
                }`}
              >
                {isCompleted ? (
                  hasErrors ? (
                    <AlertTriangle size={20} className="text-yellow-600" />
                  ) : (
                    <CheckCircle size={20} className="text-green-600" />
                  )
                ) : (
                  <Clock size={20} className="text-blue-600" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {isCompleted
                    ? "Migration Completed"
                    : "Migration in Progress"}
                </h3>
                <p className="text-sm text-gray-500">Status: {status}</p>
              </div>
            </div>
            <div className="text-right">
              {/* ✅ FIXED: Show both completion and success rate */}

              <div className="text-xs text-green-600 font-medium">
                {progressPercentage}% Success Rate
              </div>
              <div className="text-xs text-gray-500">
                Processing Time: {processingTime}
              </div>
            </div>
          </div>

          {/* ✅ FIXED: Progress bar now reflects success rate */}
          <div className="mb-4">
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className={`h-3 rounded-full transition-all duration-300 ${getProgressBarColor()}`}
                style={{ width: `${Math.min(progressPercentage, 100)}%` }}
              />
            </div>
          </div>

          {/* ✅ ENHANCED: Better progress description */}
          <div className="flex justify-between text-sm text-gray-600">
            <span>
              Processed {processed || 0} of {total || 0}
            </span>
            <span>
              Success: {successRate || 0}% | Errors: {errorRate || 0}%
            </span>
          </div>

          {/* ✅ NEW: Additional progress indicators */}
          {isCompleted && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center space-x-4">
                  <span className="text-green-600">
                    ✓ {Math.round((successRate / 100) * processed)} successful
                  </span>
                  {hasErrors && (
                    <span className="text-red-600">
                      ✗ {Math.round((errorRate / 100) * processed)} failed
                    </span>
                  )}
                </div>
                <div className="text-gray-500">
                  {isCompleted ? "Migration finished" : "In progress..."}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Logs Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Migration Logs */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Migration Logs
              </h3>
            </div>
            <div className="p-6">
              <div className="bg-gray-900 text-green-400 font-mono text-sm p-4 rounded-lg h-80 overflow-y-auto">
                {/* ✅ FIXED: Better log rendering with safety checks */}
                {migrationLogs ? (
                  migrationLogs.split("\n").map((log, index) => (
                    <div key={index} className="mb-1">
                      {log.includes("Error") ||
                      log.includes("error") ||
                      log.includes("❌") ? (
                        <span className="text-red-400">{log}</span>
                      ) : log.includes("success") || log.includes("✅") ? (
                        <span className="text-green-400">{log}</span>
                      ) : log.includes("Status:") || log.includes("Polling") ? (
                        <span className="text-blue-400">{log}</span>
                      ) : log.includes("⚠️") ? (
                        <span className="text-yellow-400">{log}</span>
                      ) : (
                        <span className="text-green-300">{log}</span>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-gray-400">No logs available</div>
                )}

                {/* ✅ FIXED: Show processing indicator only when actually processing */}
                {!isCompleted && migrationData && (
                  <div className="text-yellow-400 animate-pulse">
                    Processing records...
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Error Log */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Error Log
                </h3>
                {hasErrors && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    {errorLogs.length} Error{errorLogs.length !== 1 ? "s" : ""}
                  </span>
                )}
              </div>
            </div>
            <div className="p-6">
              <div className="h-80 overflow-y-auto">
                {hasErrors ? (
                  <div className="space-y-4">
                    {errorLogs.map((error, index) => (
                      <div
                        key={index}
                        className="border border-red-200 rounded-lg p-4 bg-red-50"
                      >
                        {/* ✅ FIXED: Handle different error message property names */}
                        <div className="text-sm font-medium text-red-900 mb-2">
                          {error.error_msg ||
                            error.errorMsg ||
                            error.message ||
                            "Unknown error"}
                        </div>
                        {error.row_data && (
                          <div className="text-xs text-gray-600">
                            <div className="font-medium mb-1">Row Data:</div>
                            <div className="bg-white p-2 rounded border max-h-20 overflow-y-auto">
                              {Object.entries(error.row_data)
                                .slice(0, 3)
                                .map(([key, value]) => (
                                  <div
                                    key={key}
                                    className="flex justify-between text-xs"
                                  >
                                    <span className="text-gray-500">
                                      {key}:
                                    </span>
                                    <span className="text-gray-900 ml-2">
                                      {value !== undefined && value !== null
                                        ? String(value)
                                        : "N/A"}
                                    </span>
                                  </div>
                                ))}
                              {Object.keys(error.row_data).length > 3 && (
                                <div className="text-xs text-gray-400 mt-1">
                                  +{Object.keys(error.row_data).length - 3} more
                                  fields...
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    <CheckCircle
                      size={24}
                      className="mx-auto mb-2 text-green-500"
                    />
                    No errors detected
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div
          className={`flex ${
            visibilityForPreviousButton ? "justify-end" : "justify-between"
          } items-center pt-6 border-t border-gray-200`}
        >
          {!visibilityForPreviousButton && ( // ✅ Only render when NOT completed
            <Button
              onClick={onPrevious}
              variant="outline"
              disabled={!isCompleted}
              className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ChevronLeft size={16} />
              <span>Previous</span>
            </Button>
          )}

          {/* ✅ FIXED: Show Next button state based on completion */}
          <Button
            onClick={onNext}
            disabled={!isCompleted}
            className={`flex items-center space-x-2 px-6 py-2 rounded-lg transition-colors ${
              isCompleted
                ? "bg-blue-600 text-white hover:bg-blue-700"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            <span>{isCompleted ? "View Report" : "Processing..."}</span>
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MigrationProgress;
